import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Plus, Trash2, FileDown, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { downloadInvoicePDF } from "@/lib/pdf";

const EMPTY = {
  vehicle_id: "", customer_id: "", sale_price: 0, tax: 0, documentation_fee: 0, registration_fee: 0,
  other_fees: 0, discount: 0, payment_method: "cash", payment_received: 0,
  sale_date: new Date().toISOString().slice(0, 10), notes: "",
};

export function SalesList() {
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const canEdit = user?.role === "admin" || user?.role === "employee";

  const load = async () => {
    const [s, v, c] = await Promise.all([api.get("/sales"), api.get("/vehicles"), api.get("/customers")]);
    setSales(s.data); setVehicles(v.data); setCustomers(c.data);
  };
  useEffect(() => { load(); }, []);

  const available = vehicles.filter((v) => v.status !== "sold");

  const submit = async () => {
    if (!form.vehicle_id || !form.customer_id) { toast.error("Pick vehicle and customer"); return; }
    if (Number(form.sale_price) <= 0) { toast.error("Sale price required"); return; }
    const payload = { ...form };
    ["sale_price", "tax", "documentation_fee", "registration_fee", "other_fees", "discount", "payment_received"]
      .forEach((k) => (payload[k] = Number(payload[k])));
    try {
      await api.post("/sales", payload);
      toast.success("Sale recorded"); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete sale? Vehicle reverts to 'ready'.")) return;
    await api.delete(`/sales/${id}`); load();
  };

  const pdf = async (sid) => {
    const r = await api.get(`/sales/${sid}`);
    downloadInvoicePDF(r.data);
  };

  return (
    <div data-testid="sales-page">
      <PageHeader
        overline="Ledger"
        title="Sales & Invoices"
        description="Recorded sales with auto gross/net profit and PDF invoices."
        action={canEdit && <Button data-testid="add-sale" onClick={() => { setForm(EMPTY); setOpen(true); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><Plus size={16} className="mr-2" />Record Sale</Button>}
      />

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Invoice", "Date", "Vehicle", "Customer", "Sale", "Total", "Balance", ""].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {sales.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-muted-foreground">No sales recorded.</td></tr>}
            {sales.map((s) => (
              <tr key={s.id} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-3 font-mono text-xs"><Link to={`/sales/${s.id}`} className="font-semibold hover:text-blue-600">{s.invoice_number}</Link></td>
                <td className="px-4 py-3">{fmtDate(s.sale_date || s.created_at)}</td>
                <td className="px-4 py-3">{s.vehicle ? `${s.vehicle.year} ${s.vehicle.make} ${s.vehicle.model}` : "—"}</td>
                <td className="px-4 py-3">{s.customer?.name || "—"}</td>
                <td className="px-4 py-3">{fmtMoney(s.sale_price)}</td>
                <td className="px-4 py-3 font-semibold">{fmtMoney(s.total)}</td>
                <td className={`px-4 py-3 font-semibold ${s.balance > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(s.balance)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`pdf-${s.id}`} onClick={() => pdf(s.id)} className="p-1.5 rounded-md hover:bg-secondary"><FileDown size={14} /></button>
                    {user?.role === "admin" && <button onClick={() => remove(s.id)} className="p-1.5 rounded-md hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Record Sale</DialogTitle>
            <DialogDescription>Select vehicle and customer, enter charges. Invoice is generated automatically.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Vehicle</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                <SelectTrigger data-testid="s-vehicle" className="rounded-lg"><SelectValue placeholder="Choose vehicle" /></SelectTrigger>
                <SelectContent>{available.map((v) => <SelectItem key={v.id} value={v.id}>{v.stock_number} · {v.year} {v.make} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="s-customer" className="rounded-lg"><SelectValue placeholder="Choose customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {[
              ["sale_price", "Sale Price"], ["documentation_fee", "Doc Fee"], ["registration_fee", "Reg Fee"],
              ["other_fees", "Other Fees"], ["tax", "Tax"], ["discount", "Discount"], ["payment_received", "Received"],
              ["sale_date", "Sale Date", "date"],
            ].map(([k, l, t]) => (
              <div key={k}><Label>{l}</Label><Input data-testid={`s-${k}`} type={t || "number"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-lg" /></div>
            ))}
            <div>
              <Label>Payment Method</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="s-method" className="rounded-lg"><SelectValue /></SelectTrigger>
                <SelectContent>{["cash", "card", "check", "finance", "wire"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Notes</Label><Textarea data-testid="s-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-lg" /></div>
          </div>
          <DialogFooter><Button data-testid="s-save" onClick={submit} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Record Sale</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ label, value, bold, big, accent }) => (
  <div className="flex justify-between items-baseline py-1">
    <span className={`${big ? "font-display text-base uppercase tracking-wider" : "text-xs text-muted-foreground uppercase tracking-wider"} ${bold ? "font-semibold" : ""}`}>{label}</span>
    <span className={`${big ? "font-display text-xl" : ""} ${bold ? "font-bold" : "font-medium"} ${accent || ""}`}>{value}</span>
  </div>
);

export function SaleDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => { api.get(`/sales/${id}`).then((r) => setData(r.data)); }, [id]);

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const { sale, vehicle, customer, costs, total, balance, gross_profit, net_profit } = data;

  return (
    <div data-testid="sale-detail-page">
      <Link to="/sales" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft size={14} /> Back to Sales
      </Link>
      <PageHeader
        overline={sale.invoice_number}
        title="Invoice"
        description={`${fmtDate(sale.sale_date || sale.created_at)} · ${sale.payment_method?.toUpperCase()}`}
        action={<Button data-testid="download-pdf" onClick={() => downloadInvoicePDF(data)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"><FileDown size={16} className="mr-2" />Download PDF</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Bill To</div>
          <div className="font-display text-xl font-bold">{customer?.name}</div>
          <div className="text-sm space-y-0.5 mt-2 text-muted-foreground">
            {customer?.phone && <div>{customer.phone}</div>}
            {customer?.email && <div>{customer.email}</div>}
            {customer?.address && <div>{customer.address}</div>}
            {customer?.driver_license && <div>DL: {customer.driver_license}</div>}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Vehicle</div>
          <div className="font-display text-xl font-bold">{vehicle?.year} {vehicle?.make} {vehicle?.model} {vehicle?.trim}</div>
          <div className="text-sm space-y-0.5 mt-2 text-muted-foreground">
            {vehicle?.vin && <div>VIN: {vehicle.vin}</div>}
            {vehicle?.color && <div>Color: {vehicle.color}</div>}
            {vehicle?.mileage ? <div>Mileage: {Number(vehicle.mileage).toLocaleString()} mi</div> : null}
            {vehicle?.stock_number && <div>Stock: {vehicle.stock_number}</div>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">Charges</div>
          <Row label="Sale Price" value={fmtMoney(sale.sale_price)} />
          {Number(sale.discount) > 0 && <Row label="Discount" value={`- ${fmtMoney(sale.discount)}`} />}
          {Number(sale.documentation_fee) > 0 && <Row label="Documentation Fee" value={fmtMoney(sale.documentation_fee)} />}
          {Number(sale.registration_fee) > 0 && <Row label="Registration Fee" value={fmtMoney(sale.registration_fee)} />}
          {Number(sale.other_fees) > 0 && <Row label="Other Fees" value={fmtMoney(sale.other_fees)} />}
          {Number(sale.tax) > 0 && <Row label="Tax" value={fmtMoney(sale.tax)} />}
          <div className="mt-3 pt-3 border-t-2 border-foreground">
            <Row label="Total Due" value={fmtMoney(total)} bold big />
          </div>
          <div className="mt-2"><Row label="Received" value={fmtMoney(sale.payment_received)} /></div>
          <div className={balance > 0 ? "text-red-600" : "text-emerald-600"}><Row label="Balance" value={fmtMoney(balance)} bold /></div>
        </Card>

        <Card className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">Internal P&L</div>
          <Row label="Purchase" value={fmtMoney(vehicle?.purchase_price)} />
          <Row label="Auction Fees" value={fmtMoney(costs?.auction_fees_total)} />
          <Row label="Repairs" value={fmtMoney(costs?.repair_total)} />
          <div className="mt-2 pt-2 border-t border-border">
            <Row label="Total Investment" value={fmtMoney(costs?.total_investment)} bold />
          </div>
          <div className={`mt-2 pt-2 border-t-2 ${gross_profit >= 0 ? "border-emerald-600 text-emerald-600" : "border-red-600 text-red-600"}`}>
            <Row label="Gross Profit" value={fmtMoney(gross_profit)} bold big />
          </div>
          <div className={`mt-2 ${net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>
            <Row label="Net Profit" value={fmtMoney(net_profit)} bold />
          </div>
        </Card>
      </div>
    </div>
  );
}
